/**
 * Image Service
 * 
 * API methods for image search and generation.
 * Uses the existing api pattern from apiService.ts.
 */

import { api } from './apiService';
import type { 
  SearchRequest, 
  SearchResults, 
  GenerateRequest, 
  GenerateResponse,
  Image 
} from '../components/images/types';

/**
 * Image service for searching and generating images
 */
export const imageService = {
  /**
   * Search for stock images from Pixabay and/or Pexels
   * 
   * @param params - Search parameters
   * @returns Promise with search results including images and pagination
   * 
   * @example
   * const results = await imageService.search({
   *   query: 'sunset mountains',
   *   source: 'both',
   *   page: 1,
   *   per_page: 12
   * });
   */
  search: async (params: SearchRequest): Promise<SearchResults> => {
    return api.post('/api/images/search', {
      query: params.query,
      source: params.source || 'both',
      image_type: params.image_type || 'all',
      page: params.page || 1,
      per_page: params.per_page || 12,
      order: params.order || 'popular',
    });
  },

  /**
   * Generate an AI image using Fal.ai
   * 
   * The backend generates the image, uploads it to S3, and returns JSON metadata
   * including the S3 key and a serving URL.
   * 
   * @param params - Generation parameters with prompt
   * @returns Promise with generated image metadata (S3 URL, key, dimensions)
   * 
   * @example
   * const result = await imageService.generate({
   *   prompt: 'A beautiful sunset over mountains with dramatic clouds'
   * });
   * // result.url → '/api/images/generated/uuid.png'
   * // result.s3_key → 'uuid.png'
   */
  generate: async (params: GenerateRequest): Promise<GenerateResponse> => {
    return api.post('/api/images/generate', {
      prompt: params.prompt,
    });
  },

  /**
   * Get image metadata by source and ID
   * 
   * @param source - Image source ('pixabay' or 'pexels')
   * @param id - Image ID from the source
   * @returns Promise with image metadata
   */
  getImageById: async (source: string, id: string): Promise<Image> => {
    return api.get(`/api/images/${source}/${id}`);
  },

  /**
   * Get the download URL for full resolution image
   * 
   * @param source - Image source ('pixabay' or 'pexels')
   * @param id - Image ID from the source
   * @param size - Optional size ('small', 'medium', 'large', 'original')
   * @returns Download URL string
   */
  getDownloadUrl: (source: string, id: string, size: string = 'original'): string => {
    return `/api/images/${source}/${id}/download?size=${size}`;
  },

  /**
   * Download an image as a Blob
   * 
   * @param source - Image source ('pixabay' or 'pexels')
   * @param id - Image ID from the source
   * @param size - Optional size ('small', 'medium', 'large', 'original')
   * @returns Promise with image Blob
   */
  downloadImage: async (source: string, id: string, size: string = 'original'): Promise<Blob> => {
    return api.get(`/api/images/${source}/${id}/download`, {
      params: { size },
      responseType: 'blob',
    });
  },

  /**
   * Persist a stock image to S3 (download from provider and upload to S3)
   * 
   * This ensures the image is permanently stored and won't expire even if provider URLs do.
   * 
   * @param source - Image source ('pixabay' or 'pexels')
   * @param id - Image ID from the provider
   * @param downloadUrl - Download URL (required for Pixabay, optional for Pexels)
   * @returns Promise with S3 key and serving URL
   * 
   * @example
   * const result = await imageService.persistStockImage('pixabay', '12345', 'https://...');
   * // result.s3_key → 'pixabay-12345-uuid.jpg'
   * // result.url → '/api/images/stock/pixabay-12345-uuid.jpg'
   */
  persistStockImage: async (source: string, id: string, downloadUrl?: string): Promise<{ s3_key: string; url: string }> => {
    return api.post('/api/images/persist', {
      source,
      id,
      download_url: downloadUrl,
    });
  },

  /**
   * Upload or ingest an image for CMS / article editor body (and OG heroes).
   * Flock stores bytes in S3 and returns a stable path served under GET /api/images/generated/{key}.
   * - `file`: multipart field `file` at POST /api/images/upload
   * - `url`: remote image to fetch and persist at POST /api/images/ingest-url (JSON body `{ "url" }`)
   */
  ingestOgImage: async (input: { file?: File; url?: string }): Promise<{ url: string; s3_key?: string }> => {
    if (input.file) {
      const body = new FormData();
      body.append('file', input.file);
      return api.post('/api/images/upload', body);
    }
    if (input.url) {
      return api.post('/api/images/ingest-url', { url: input.url });
    }
    throw new Error('ingestOgImage: provide file or url');
  },
};

export default imageService;

